import { useState, useEffect } from 'react';
import { Form, Modal, Input, Radio, Button, Badge, Space, InputNumber, Select, Popover, Typography } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import Axios from 'axios';
import moment from 'moment';
import { Config } from '../config_section.js';
import REWIREskillSelectionModalForm from '../../admin/modals/REWIREskillSelectionModalForm.js';

import areas from './initStructure/SpartaAreas.js';
import topics from './initStructure/SpartaTopics.js';

import { useWatch } from 'antd/lib/form/Form.js';

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

const AddEditCourseModalForm = ({ editRecord, saveLocally, onCancel, refresh, showMessage, rsSkillsGroup, rsSpecificKnowledge, rsSpecificSkills }) => {
  const [modalLoading, setModalLoading] = useState(false);
  const [form] = Form.useForm();
  const [isNew, setIsNew] = useState(false);
  const [skillModalShown, setSkillModalShown] = useState(false);
  const [descLoading, setDescLoading] = useState(false);
  const [skillFromData, setSkillFromData] = useState();

  const [topicsData, setTopicsData] = useState([]);
  const [currentTopic, setCurrentTopic] = useState('');
  const [currentTopicPer, setCurrentTopicPer] = useState('');

  const desc = useWatch('description', form);


  useEffect(() => {
    if (typeof editRecord === 'object' && editRecord !== null && form.getFieldValue('id') !== editRecord.id) {
      let initialData = JSON.parse(JSON.stringify(editRecord));
      delete initialData.key;
      form.setFieldsValue(initialData);
      setIsNew(false);

      setTopicsData(editRecord.topics || []);
      setCurrentTopic('');
      setCurrentTopicPer('');

      let initialSkillFormData = {
        skills_group: editRecord.skills_group || [],
        specific_skills: editRecord.specific_skills || [],
        specific_knowledge: editRecord.specific_knowledge || [],
      };
      setSkillFromData(initialSkillFormData);
    } else if (editRecord === 'new') {
      form.resetFields();
      setIsNew(true);

      setTopicsData([]);
      setCurrentTopic('');
      setCurrentTopicPer('');

      setSkillFromData({
        skills_group: [],
        specific_skills: [],
        specific_knowledge: [],
      });
    }
  }, [editRecord, form]);

  const addTopic = () => {
    if (currentTopic) {
      const newTopic = {
        topicId: currentTopic - 1,
        ectsPer: currentTopicPer ? currentTopicPer / 100 : 1,
      };
      const updated = [...topicsData, newTopic];
      setTopicsData(updated);
      form.setFieldsValue({ topics: updated });
      setCurrentTopic('');
      setCurrentTopicPer('');
    }
  };

  const editTopic = (index) => {
    const t = topicsData[index];
    setCurrentTopic(t.topicId + 1);
    setCurrentTopicPer(t.ectsPer * 100);
    const filtered = topicsData.filter((_, i) => i !== index);
    setTopicsData(filtered);
    form.setFieldsValue({ topics: filtered });
  };

  const removeTopic = (index) => {
    const filtered = topicsData.filter((_, i) => i !== index);
    setTopicsData(filtered);
    form.setFieldsValue({ topics: filtered });
  };

  async function analyzeDescription() {
    setDescLoading(true);
    try {
      const rsp = await Axios.post(Config.server.analyzeCourseDescription, { description: form.getFieldValue('description') }, { headers: { 'Content-Type': 'application/json' } });
      if (rsp.data && rsp.data.error === false) {
        setSkillFromData((fd) => ({
          ...fd,
          skills_group: rsp.data.data.prediction.skills_groups,
        }));
        showMessage({
          type: 'success',
          content: 'Description analysis done.',
        });
      } else {
        showMessage({
          type: 'error',
          content: 'Something went wrong!',
        });
      }
    } catch (e) {
      console.error(e);
      showMessage({
        type: 'error',
        content: 'Something went wrong!',
      });
    } finally {
      setDescLoading(false);
    }
  }

  return (
    <Modal
      open={editRecord !== null}
      width={850}
      maskClosable={false}
      centered
      className={isNew ? 'add-entity-modal' : 'edit-entity-modal'}
      title={isNew ? 'Add a new course' : 'Edit course'}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={modalLoading}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => {
        form.validateFields().then((values) => {
          setModalLoading(true);
          values.skills_group = skillFromData.skills_group || [];
          values.specific_skills = skillFromData.specific_skills || [];
          values.specific_knowledge = skillFromData.specific_knowledge || [];

          const formData = {
            ...values,
            created: values.created ? values.created : moment().format('YYYY-MM-DD HH:mm:ss'),
            updated: moment().format('YYYY-MM-DD HH:mm:ss'),
          };
          saveLocally(formData);
          refresh();
          setModalLoading(false);
        });
      }}
    >
      <Form form={form} layout="horizontal" name={isNew ? 'add-course-form' : 'edit-course-form'} labelCol={{ span: 7 }} wrapperCol={{ span: 15 }} labelWrap colon={false}>
        <Form.Item name="name" label="Course name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="type" label="Type" rules={[{ required: true }]}>
          <Radio.Group>
            <Radio value="mandatory">Mandatory</Radio>
            <Radio value="voluntary">Voluntary</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="semester" label="Semester" rules={[{ required: true }]}>
          <Radio.Group>
            <Radio value="winter">Winter</Radio>
            <Radio value="summer">Summer</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="training" label="Training" rules={[{ required: true }]}>
          <Radio.Group>
            <Radio value="Yes">Yes</Radio>
            <Radio value="No">No</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="credits" label="ECTS Credits" rules={[{ required: true }]}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Topics (ECTS %)">
          <Space align="start">
            <Select value={currentTopic} onChange={(val) => setCurrentTopic(val)} placeholder="Select topic" style={{ width: 200 }}>
              {topics.map((topic, idx) => (
                <Option key={idx + 1} value={idx + 1}>
                  {topic.name}
                </Option>
              ))}
            </Select>
            {currentTopic && (
              <>
                <InputNumber
                  min={0}
                  max={100}
                  value={currentTopicPer || 100}
                  onChange={(val) => setCurrentTopicPer(val)}
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace('%', '')}
                />
                <Button type="dashed" onClick={addTopic}>
                  Save
                </Button>
              </>
            )}
          </Space>
          <div style={{ marginTop: 8 }}>
            {topicsData.map((t, idx) => {
              const topicObj = topics[t.topicId];
              return (
                <Space key={idx} style={{ marginBottom: 4 }}>
                  <Badge color={areas[topicObj.areaId].color} text={`${t.ectsPer * 100}% ${topicObj.name}`} />
                  <Button type="text" icon={<EditOutlined />} onClick={() => editTopic(idx)} />
                  <Button type="text" icon={<DeleteOutlined />} onClick={() => removeTopic(idx)} />
                </Space>
              );
            })}
          </div>
          <Form.Item name="topics" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
          rules={[
            {
              required: true,
              message: 'Please enter the description',
            },
          ]}
        >
          <TextArea rows={5} />
        </Form.Item>
        <Form.Item label="Analyze description" name="analyze_desc">
          <Space>
            <Button onClick={analyzeDescription} loading={descLoading} disabled={!desc || !desc.trim()}>
              Analyze
            </Button>
          </Space>
        </Form.Item>
        <Form.Item name="skills_group" label="Skills sroups">
          <Space direction="vertical" size={16}>
            <Space wrap className="skillBadges">
              {skillFromData?.skills_group.map((skillGroup) => {
                const grp = rsSkillsGroup.find((g) => g.id === skillGroup);
                return (
                  <Popover
                    getPopupContainer={() => document.body}
                    overlayStyle={{
                      position: 'fixed',
                      overflow: 'visible',
                    }}
                    content={
                      <div className="skills-popover">
                        <Title level={4}>{grp.name}</Title>
                        <Title level={5}>Included Skills</Title>
                        <ul>{grp.specific_skills.length > 0 ? grp.specific_skills.map((sg, i) => <li key={i}>{rsSpecificSkills.find((e) => e.id === sg)?.name}</li>) : <span className='empty'>None</span>}</ul>
                        <Title level={5}>Included Knowledge</Title>
                        <ul>{grp.specific_knowledge.length > 0 ? grp.specific_knowledge.map((sg, i) => <li key={i}>{rsSpecificKnowledge.find((e) => e.id === sg)?.name}</li>) : <span className='empty'>None</span>}</ul>
                      </div>
                    }
                  >
                    <Badge count={grp.name} color={grp.color || 'white'} key={skillGroup} className="skillBadge" />
                  </Popover>
                );
              })}
            </Space>
            <Button onClick={() => setSkillModalShown(true)}>Choose</Button>
          </Space>
        </Form.Item>
      </Form>

      <REWIREskillSelectionModalForm
        skillModalShown={skillModalShown}
        initialData={skillFromData}
        onCancel={() => setSkillModalShown(false)}
        saveFormData={(data) => setSkillFromData(data)}
        rsSkillsGroup={rsSkillsGroup}
        rsSpecificKnowledge={rsSpecificKnowledge}
        rsSpecificSkills={rsSpecificSkills}
      />
    </Modal>
  );
};

export default AddEditCourseModalForm;
